import React, { useState, useEffect, useContext } from 'react'
import { useNotify } from '../components/notice/useNotify'
import { UserContext } from '../components/UserContext'
import PersonaCard from '../components/PersonaCard'
import PersonaCreator from '../components/PersonaCreator'
import CreateAdvisorCard from '../components/CreateAdvisorCard'
import {
  getPersonas,
  addPersonaToSquad,
  Persona,
} from '../api/client'

// The admin catalog: every system advisor not already in this user's squad,
// flat and all at once. /squad reaches the same advisors, but split into
// sections, capped, and behind "Load More" and per-category expanders.
// `/personas` already leaves out squad members (matched on parent id, since
// adding clones the persona), so an added advisor simply drops off the list.
const AllAdvisorsPage: React.FC = () => {
  const notify = useNotify()
  const { user } = useContext(UserContext)
  const [personas, setPersonas] = useState<Persona[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdvisorCreator, setShowAdvisorCreator] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        setPersonas(await getPersonas())
      } catch (err) {
        notify.error('Failed to load personas')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleAddToSquad = async (persona: Persona) => {
    try {
      // Call the actual API endpoint
      await addPersonaToSquad(persona.id)

      setPersonas(await getPersonas())
      
      notify.success(`Added ${persona.first_name} to your squad!`)
    } catch (err) {
      notify.error(`Failed to add ${persona.first_name} to your squad`)
      console.error(err)
    }
  }

  // Handler for persona updates
  const handlePersonaUpdated = async () => {
    try {
      setPersonas(await getPersonas())
    } catch (err) {
      notify.error('Failed to update the squad with the updated advisor');
      console.error(err);
    }
  };
  
  const handleAdvisorCreated = async (persona: Persona) => {
    try {
      setPersonas(await getPersonas())
      
      notify.success(`Successfully created advisor ${persona.first_name}`)
    } catch (err) {
      notify.error('Failed to update squad with the new advisor')
      console.error(err)
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      {/* Advisor Creator Modal */}
      <PersonaCreator
        isOpen={showAdvisorCreator}
        onClose={() => setShowAdvisorCreator(false)}
        onPersonaCreated={handleAdvisorCreated}
      />
      
      <div className="container mx-auto px-4 pb-8 sm:pt-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
            All Advisors
          </h1>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <CreateAdvisorCard onClick={() => setShowAdvisorCreator(true)} />
            
            {personas.map(persona => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                onPersonaUpdated={handlePersonaUpdated}
                isAdmin={user?.isAdmin}
                actionButtons={
                  <button
                    onClick={() => handleAddToSquad(persona)}
                    className="px-3 py-2 bg-action text-plum rounded-md hover:bg-action-dark"
                  >
                    Add to Squad
                  </button>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default AllAdvisorsPage